import { Injectable, computed, signal } from '@angular/core';
import type { AuthMeDto, AuthTokensDto, UserDto } from '@tattva/types';
import { FamilyMemberRole } from '@tattva/constants';

const ACCESS_KEY = 'tattva.accessToken';
const REFRESH_KEY = 'tattva.refreshToken';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly meSignal = signal<AuthMeDto | null>(null);
  private readonly accessTokenSignal = signal<string | null>(localStorage.getItem(ACCESS_KEY));
  private readonly refreshTokenSignal = signal<string | null>(localStorage.getItem(REFRESH_KEY));

  readonly me = this.meSignal.asReadonly();
  readonly accessToken = this.accessTokenSignal.asReadonly();
  readonly isAuthenticated = computed(() => Boolean(this.accessTokenSignal()));
  readonly user = computed(() => this.meSignal()?.user ?? null);
  readonly primaryFamilyId = computed(() => this.meSignal()?.familyMemberships[0]?.familyId ?? null);
  readonly primaryRole = computed(() => this.meSignal()?.familyMemberships[0]?.role ?? null);

  setTokens(tokens: AuthTokensDto): void {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
    this.accessTokenSignal.set(tokens.accessToken);
    this.refreshTokenSignal.set(tokens.refreshToken);
  }

  setMe(me: AuthMeDto): void {
    this.meSignal.set(me);
  }

  setUser(user: UserDto): void {
    const current = this.meSignal();
    if (current) {
      this.meSignal.set({ ...current, user });
    }
  }

  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    this.accessTokenSignal.set(null);
    this.refreshTokenSignal.set(null);
    this.meSignal.set(null);
  }

  getRefreshToken(): string | null {
    return this.refreshTokenSignal();
  }

  isParentRole(): boolean {
    const role = this.primaryRole();
    return role === FamilyMemberRole.PRIMARY_PARENT || role === FamilyMemberRole.SECONDARY_PARENT;
  }

  isGrandparentRole(): boolean {
    const role = this.primaryRole();
    return role === FamilyMemberRole.GRANDFATHER || role === FamilyMemberRole.GRANDMOTHER;
  }

  isStudentRole(): boolean {
    return this.primaryRole() === FamilyMemberRole.STUDENT;
  }
}
